import { Outlet, useParams } from 'react-router-dom';

import { Component as NotFound } from '@/pages/not-found/Page';
import { FullPageLoader } from '@/shared/components';
import { useIsFinanceWorkspace } from '@/shared/hooks/use-is-finance-workspace';

/**
 * Layout route for the finance experience: its sole cross-route concern today
 * is access control. The finance surfaces (`/finance/*`) must render ONLY when
 * the finance experience is active — a finance-typed workspace
 * (`WORKSPACE_TYPE_FINANCE`) with the `enable_finance_workspace` Statsig gate on
 * — otherwise a user could reach a finance page in any workspace by typing the
 * URL. Wrapping the whole finance route group keeps the check in one place,
 * inherited by every current and future `/finance/*` route. Any shared finance
 * chrome would live here too.
 *
 * Both conditions (gate + workspace type) live in `useIsFinanceWorkspace`, the
 * single source of truth shared with the sidebar — so `isFinanceWorkspace` here
 * already means "gate on AND finance-typed", and the gate cannot be forgotten
 * at this boundary. When the gate is off the hook disables its fetch and
 * reports `isFinanceWorkspace: false` immediately, so we decide without waiting.
 *
 * Ids are read from the URL (`useParams`), NOT the user-preferences store: the
 * store lags the URL during a workspace switch, which would make the check
 * misfire and bounce the user mid-switch.
 */
export function FinanceLayout() {
  const { orgId, workspaceId } = useParams<{
    orgId: string;
    workspaceId: string;
  }>();
  const ready = Boolean(orgId && workspaceId);

  const { isFinanceWorkspace, isLoading, isError, error } =
    useIsFinanceWorkspace(orgId, workspaceId);

  // The workspace fetch failed (retry: false), so we can't confirm the type.
  // Surface the standard workspace error page rather than guessing — the
  // WORKSPACE route's RouteErrorBoundary catches this throw.
  if (ready && isError) {
    throw error ?? new Error('Failed to resolve workspace');
  }

  // Still resolving the workspace — don't decide yet.
  if (!ready || isLoading) {
    return <FullPageLoader />;
  }

  // Keep unavailable Finance URLs visible for recovery and diagnostics.
  if (!isFinanceWorkspace) {
    return <NotFound />;
  }

  return <Outlet />;
}
